import "../administration/administration.css";

import { nextSort, sortRows } from "../administration/sortable";
import {
  requestCreateApplication,
  requestDeleteApplication,
  requestUpdateApplication,
} from "../services/backendRequests";
import { useMemo, useState } from "react";

import { AdminTable } from "../administration/AdminTable";
import { ApplicationCampaignsDrawer } from "./ApplicationCampaignsDrawer";
import { ApplicationDrawer } from "./ApplicationDrawer";
import { Button } from "reactstrap";
import CopyButton from "../components/copyButton";
import { EmptyState } from "../components/EmptyState";
import { FilterBar } from "../clientDashboard/components/FilterBar";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import { hasAdminAccess } from "../services/roles";
import { useFetch } from "../hooks/useFetch";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

const sortValue = (application, key) => {
  switch (key) {
    case "company":
      return application.company.name;
    case "campaigns":
      return application.campaigns.length;
    case "active":
      return application.active ? 1 : 0;
    default:
      return application.name;
  }
};

// The applications (public API consumers) and their campaign access.
const Applications = () => {
  const user = useSelector((state) => state.user);
  const isAdmin = hasAdminAccess(user.self);
  const { enqueueSnackbar } = useSnackbar();
  const [applications, { loading, refetch }] = useFetch("/api/applications");
  const [companies] = useFetch("/api/companies");

  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState([]);
  const [sort, setSort] = useState({ sortKey: "name", sortDir: "asc" });
  // { type: "form" | "campaigns", application }
  const [drawer, setDrawer] = useState(null);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = (applications ?? []).filter(
      (application) =>
        (!needle ||
          application.name.toLowerCase().includes(needle) ||
          application.keycloak_uuid.includes(needle)) &&
        (companyFilter.length === 0 ||
          companyFilter.includes(application.company.id)) &&
        (campaignFilter.length === 0 ||
          application.campaigns.some(({ id }) => campaignFilter.includes(id)))
    );
    return sortRows(filtered, sort.sortKey, sort.sortDir, sortValue);
  }, [applications, search, companyFilter, campaignFilter, sort]);

  const companyOptions = useMemo(
    () =>
      // Only companies that actually own an application, like the campaign filters
      [
        ...new Map(
          (applications ?? []).map(({ company }) => [company.id, company])
        ).values(),
      ]
        .map(({ id, name }) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [applications]
  );

  // Only the campaigns actually granted somewhere: filtering on a campaign nobody can read
  // would always return an empty table
  const campaignOptions = useMemo(
    () =>
      [
        ...new Map(
          (applications ?? []).flatMap(({ campaigns }) =>
            campaigns.map((campaign) => [campaign.id, campaign])
          )
        ).values(),
      ]
        .map(({ id, name }) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [applications]
  );

  const submitApplication = async (companyId, payload) => {
    const application = drawer.application;
    if (application.id)
      await requestUpdateApplication(application.id, payload, user);
    else await requestCreateApplication(companyId, payload, user);

    setDrawer(null);
    refetch();
    enqueueSnackbar(
      `Application ${payload.name} ${application.id ? "updated" : "created"}.`,
      { variant: "success" }
    );
  };

  const deleteApplication = (application) => {
    if (
      !window.confirm(
        `Delete application ${application.name} and its access to ${application.campaigns.length} campaign(s)?`
      )
    )
      return;

    requestDeleteApplication(application.id, user)
      .then(() => {
        refetch();
        enqueueSnackbar(`Application ${application.name} deleted.`, {
          variant: "success",
        });
      })
      .catch((error) => enqueueSnackbar(String(error), { variant: "error" }));
  };

  const onCampaignsSaved = (count) => {
    setDrawer(null);
    refetch();
    enqueueSnackbar(`${count} campaign(s) updated.`, { variant: "success" });
  };

  const columns = [
    {
      key: "active",
      label: "Active",
      sortable: true,
      cell: (application) =>
        application.active ? (
          <i
            className="fa-solid fa-lg fa-check text-success"
            aria-hidden="true"
          />
        ) : (
          <i
            className="fa-solid fa-lg fa-xmark text-danger"
            aria-hidden="true"
          />
        ),
    },
    { key: "name", label: "Name", className: "w-25", sortable: true },
    {
      key: "company",
      label: "Company",
      sortable: true,
      cell: (a) => a.company.name,
    },
    {
      key: "keycloak_uuid",
      label: "Keycloak UUID",
      cell: (application) => (
        <span className="d-flex align-items-center gap-1">
          <code>{application.keycloak_uuid}</code>
          <CopyButton
            value={application.keycloak_uuid}
            datatip="Copy Keycloak UUID"
          />
        </span>
      ),
    },
    {
      key: "campaigns",
      label: "Campaigns",
      sortable: true,
      cell: (application) => (
        <>
          {application.campaigns.length}
          <Button
            data-tooltip-id="tooltip"
            data-tooltip-content="Update campaign access"
            className="ms-2 p-0"
            color="link"
            onClick={() => setDrawer({ type: "campaigns", application })}
          >
            <i className="fa-solid fa-lg fa-key" />
          </Button>
        </>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: "Actions",
            cell: (application) => (
              <div className="admin-actions">
                <Button
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Edit application"
                  color="link"
                  onClick={() => setDrawer({ type: "form", application })}
                >
                  <i className="fa-solid fa-lg fa-pencil" />
                </Button>
                <Button
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Delete application"
                  data-tooltip-variant="error"
                  className="admin-actions__danger"
                  color="link"
                  onClick={() => deleteApplication(application)}
                >
                  <i className="fa-solid fa-lg fa-trash-can" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {drawer?.type === "form" && (
        <ApplicationDrawer
          application={drawer.application}
          companies={companies ?? []}
          onClose={() => setDrawer(null)}
          onSubmit={submitApplication}
        />
      )}
      {drawer?.type === "campaigns" && (
        <ApplicationCampaignsDrawer
          application={drawer.application}
          // Refetch on close too: a save can fail halfway through its campaigns, and the row
          // must not keep showing a list that is no longer the truth
          onClose={() => {
            setDrawer(null);
            refetch();
          }}
          onSaved={onCampaignsSaved}
        />
      )}

      <div className="page-content container-fluid cf-page">
        {/* No metrics: the FilterBar right below already shows the count */}
        <LevelHeader path={[{ label: "Applications" }]} />

        <FilterBar
          query={search}
          onQuery={setSearch}
          placeholder="Search an application…"
          count={rows.length}
          countLabel="application"
          selects={[
            {
              label: "Company",
              value: companyFilter,
              onChange: setCompanyFilter,
              allLabel: "All companies",
              options: companyOptions,
            },
            {
              label: "Campaign",
              value: campaignFilter,
              onChange: setCampaignFilter,
              allLabel: "All campaigns",
              options: campaignOptions,
            },
          ]}
          trailing={
            isAdmin && (
              <Button
                color="primary"
                size="sm"
                onClick={() => setDrawer({ type: "form", application: {} })}
              >
                <i className="fa-solid fa-plus me-1" /> New application
              </Button>
            )
          }
        />

        {!loading && rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-plug"
            title="No application"
            text="Create an application to give a client access to the public API."
          />
        ) : (
          <AdminTable
            columns={columns}
            rows={rows}
            loading={loading}
            sortKey={sort.sortKey}
            sortDir={sort.sortDir}
            onSort={(column) => setSort((prev) => nextSort(prev, column))}
          />
        )}
      </div>
    </>
  );
};

export default Applications;
